// Single source of truth for the city-page tool funnel links + attribution.
// Both CityTools and CityHeroCta build their hrefs here, and the regression test
// (__tests__/lib/city-tool-links.test.ts) asserts every generated link is a valid
// route AND carries `fromTool` attribution -- the pilot's make-or-break metric
// (obsidian/research/city-page-seo-pilot-2026-06.md). Keeping the link facts here
// (separate from the components' icons/labels) lets the test stay free of React.

import { buildToolUrl, type Season } from '@/lib/result-tool-urls'

export type CityToolPrefill = 'grass-zip' | 'location' | 'none'

export interface CityToolLink {
  /** analytics target id */
  id: string
  /** buildToolUrl registry id; null = plain href (the interactive /diagnose flow) */
  toolId: string | null
  href: string
  prefill: CityToolPrefill
}

export interface CityToolCtx {
  slug: string
  cityName: string
  /** state code, e.g. "IL" (used only by the location-prefill soil-temp tool) */
  state: string
  /** lead grass guide slug, e.g. "bermudagrass" */
  grass?: string
  /** representative city ZIP */
  zip?: string
}

// The funnel, in display order. The compact "quick" strip filters this list in the
// component; the routes + prefill strategy live here so they can't silently drift.
export const CITY_TOOL_LINKS: CityToolLink[] = [
  { id: 'diagnose', toolId: null, href: '/diagnose', prefill: 'none' },
  { id: 'soil-temperature', toolId: 'soil-temperature', href: '/tools/soil-temperature', prefill: 'location' },
  { id: 'fertilizer-calculator', toolId: 'fertilizer-calculator', href: '/tools/fertilizer-calculator', prefill: 'grass-zip' },
  { id: 'watering-schedule', toolId: 'watering-schedule', href: '/tools/watering-schedule', prefill: 'grass-zip' },
  { id: 'seeding-calculator', toolId: 'seeding-calculator', href: '/tools/seeding-calculator', prefill: 'grass-zip' },
  { id: 'aeration', toolId: 'aeration-timing-tool', href: '/tools/aeration-calculator', prefill: 'grass-zip' },
]

/** PostHog source id for a city page, e.g. "city_chicago-il". */
export function citySource(slug: string): string {
  return `city_${slug}`
}

/** Build the funnel href for one city tool, always carrying `fromTool` attribution. */
export function cityToolHref(t: CityToolLink, ctx: CityToolCtx): string {
  const source = citySource(ctx.slug)
  // /diagnose isn't in the buildToolUrl registry, but it still reads ?fromTool
  // (via useFromTool) for landing-side attribution -- carry the source param so
  // the page's biggest CTA isn't a measurement blind spot.
  if (!t.toolId) return `${t.href}?fromTool=${encodeURIComponent(source)}`
  if (t.prefill === 'location') {
    return buildToolUrl(t.toolId, { location: `${ctx.cityName}, ${ctx.state}`, sourceToolId: source })
  }
  if (t.prefill === 'grass-zip') {
    return buildToolUrl(t.toolId, { grassType: ctx.grass, zip: ctx.zip, sourceToolId: source })
  }
  return buildToolUrl(t.toolId, { sourceToolId: source })
}

/** Hero primary CTA: the grass-ID app funnel. */
export function cityDiagnoseHref(slug: string): string {
  return `/diagnose?fromTool=${encodeURIComponent(citySource(slug))}`
}

/**
 * Hero secondary CTA: the city's pre-filled fertilizer schedule.
 * `season` is optional -- when supplied (by the season-aware CTA), it opens the
 * calculator configured for the season the copy promises, so the sentence and the
 * tool can't drift. Omitting it preserves the original behavior (no season param).
 */
export function cityFertilizerHref(ctx: {
  slug: string
  grass?: string
  zip?: string
  season?: Season
}): string {
  return buildToolUrl('fertilizer-calculator', {
    grassType: ctx.grass,
    zip: ctx.zip,
    season: ctx.season,
    sourceToolId: citySource(ctx.slug),
  })
}
